<template>
  <div class="p-4 relative">
    <PlanOverlay
      :visible="ressourcesPerm == 'none'"
      :reason="ressourcesPerm == 'none' && isSubUser ? 'permission' : 'plan'"
    />
    <!-- En-tête avec la barre de recherche et le bouton d'ajout -->
    <div class="flex justify-between items-center mb-4">
      <h1 class="m-0 text-2xl select-none font-bold">Ressources humaines</h1>
      <button class="callToAction !px-4 select-none"
        v-if="perm().ressources == 'modification' && canAddRessource"
        @click="selectedRessource=null; popupVisible=true">
        Ajouter une ressource
      </button>
    </div>
    
    <!-- Barre de recherche -->
    <div class="relative mb-4">
      <input 
        type="text" 
        v-model="searchQuery" 
        placeholder="Rechercher une ressource..." 
        class="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-blue-300"
      />
      <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
        <i class="fas fa-search"></i>
      </span>
      <span 
        v-if="searchQuery.trim().length" 
        class="cursor-pointer absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
        @click="searchQuery = ''"
      >
        <i class="fas fa-times"></i>
      </span>
    </div>

    <!-- Filtres select et bouton de réinitialisation -->
    <div class="flex gap-2 mb-4 w-4/5 h-9">
      <select v-model="selectedTeam" class="border border-gray-300 rounded-lg p-2 w-full">
        <option value="">Toutes les équipes</option>
        <option v-for="team in uniqueTeams" :key="team" :value="team">{{ team }}</option>
      </select>
      <select v-model="selectedSector" class="border border-gray-300 rounded-lg p-2 w-full">
        <option value="">Tous les secteurs</option>
        <option v-for="sector in uniqueSectors" :key="sector" :value="sector">{{ sector }}</option>
      </select>
      <select v-model="selectedService" class="border border-gray-300 rounded-lg p-2 w-full">
        <option value="">Tous les services</option>
        <option v-for="service in uniqueServices" :key="service" :value="service">{{ service }}</option>
      </select>
      <select v-model="selectedPosition" class="border border-gray-300 rounded-lg p-2 w-full">
        <option value="">Tous les postes</option>
        <option v-for="position in uniquePositions" :key="position" :value="position">{{ position }}</option>
      </select>
      <button class="flex items-center h-full w-fit px-4 font-normal select-none whitespace-nowrap" @click="resetFilters">
        Réinitialiser les filtres
      </button>
    </div>

    <!-- Tableau des ressources -->
    <div class="overflow-x-auto bg-white">
      <table class="min-w-full border border-gray-200 shadow-md rounded-lg bg-white">
        <thead class="bg-[#1E1017] text-white font-bold text-left">
          <tr>
            <th class="px-4 py-2 border-b">Nom</th>
            <th class="px-4 py-2 border-b">Prénom</th>
            <th class="px-4 py-2 border-b">Équipe</th>
            <th class="px-4 py-2 border-b">Secteur</th>
            <th class="px-4 py-2 border-b">Service</th>
            <th class="px-4 py-2 border-b">Poste</th>
            <th class="px-4 py-2 border-b w-5 !text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ressource in filteredResources" :key="ressource.id" class="hover:bg-red-100 text-left even:bg-red-50 odd:bg-white"
              @dblclick="selectedRessource = ressource; popupVisible = true">
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700">{{ ressource.lastName }}</td>
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700">{{ ressource.firstName }}</td>
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700">{{ ressource.team }}</td>
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700">{{ ressource.sector }}</td>
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700">{{ ressource.service }}</td>
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700">{{ ressource.position }}</td>
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700 w-5 !text-center">
              <div class="flex items-center justify-center gap-1">
                <button class="callToAction !px-4 cursor-pointer select-none" @click="selectedRessource = ressource; popupVisible = true">
                  Modifier
                </button>
                <button
                  class="text-gray-500 hover:text-primary !m-0 !mr-1 !ml-2"
                  title="Monter"
                  @click.stop="moveRessourceUp(ressource)"
                  @dblclick.stop
                  :disabled="filteredResources[0]?.id === ressource.id"
                >
                  <i class="fas fa-arrow-up"></i>
                </button>
                <button
                  class="text-gray-500 hover:text-primary !m-0"
                  title="Descendre"
                  @click.stop="moveRessourceDown(ressource)"
                  @dblclick.stop
                  :disabled="filteredResources[filteredResources.length - 1]?.id === ressource.id"
                >
                  <i class="fas fa-arrow-down"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Popup pour ajouter/modifier une ressource -->
    <PopupRessource
      :visible="popupVisible"
      :ressource="selectedRessource"
      :ressources="ressources"
      @close="popupVisible = false"
      @add="handleAddRessource"
      @update="handleUpdateRessource"
      @delete="handleDeleteRessource"
    />
  </div>
</template>

<script>
import { fetchRessources, addRessource, updateRessource, deleteRessource } from '@/services/ressourceService';
import Header from '@/components/Others/Header.vue';
import PopupRessource from '@/components/Popups/PopupRessource.vue';
import RessourceItem from '@/components/Ressource/RessourceItem.vue';
import PlanOverlay from '@/components/Others/PlanOverlay.vue';
import { inject } from 'vue';

export default {
  name: "ResourcePage",
  components: {
    Header,
    PopupRessource,
    RessourceItem,
    PlanOverlay,
  },
  data() {
    return {
      ressources: [],
      maxRessources: Infinity,
      popupVisible: false,
      selectedRessource: null,
      searchQuery: "",
      selectedTeam: "",
      selectedPosition: "",
      selectedService: "",
      selectedSector: ""
    };
  },
  setup(){
    const perm = inject('userPerm');
    const currentUser = inject('currentUser');
    return { perm, currentUser };
  },
  computed: {
    ressourcesPerm() {
      return this.perm().ressources;
    },
    isSubUser() {
      const user = this.currentUser && this.currentUser();
      return user ? !!user.mainUserId : false;
    },
    filteredResources() {
      let result = this.ressources;
      // Filtre par recherche (full name)
      if (this.searchQuery.trim()) {
        const search = this.searchQuery.toLowerCase();
        result = result.filter(res =>
          (`${res.firstName || ''} ${res.lastName || ''}`).toLowerCase().includes(search)
        );
      }
      // Filtre par team
      if (this.selectedTeam) {
        result = result.filter(res => res.team === this.selectedTeam);
      }
      // Filtre par position
      if (this.selectedPosition) {
        result = result.filter(res => res.position === this.selectedPosition);
      }
      // Filtre par service
      if (this.selectedService) {
        result = result.filter(res => res.service === this.selectedService);
      }
      // Filtre par sector
      if (this.selectedSector) {
        result = result.filter(res => res.sector === this.selectedSector);
      }
      return result;
    },
    uniqueTeams() {
      return Array.from(new Set(this.ressources.map(r => r.team).filter(val => !!val)));
    },
    uniquePositions() {
      return Array.from(new Set(this.ressources.map(r => r.position).filter(val => !!val)));
    },
    uniqueServices() {
      return Array.from(new Set(this.ressources.map(r => r.service).filter(val => !!val)));
    },
    uniqueSectors() {
      return Array.from(new Set(this.ressources.map(r => r.sector).filter(val => !!val)));
    },
    canAddRessource() {
      if (this.ressourcesPerm !== 'modification') return false;
      const max = this.maxRessources ?? Infinity;
      return this.ressources.length < max;
    },
  },
  methods: {
    async fetchRessources() {
      this.ressources = await fetchRessources();
      this.ressources.forEach(r => {
        r.isEditing = false;
      });
      try {
        const { getEffectiveUserId, getUserById } = await import('@/services/userService');
        const userId = await getEffectiveUserId();
        const user = await getUserById(userId);
        this.maxRessources = user.plan?.maxRessources ?? Infinity;
      } catch (e) {
        this.maxRessources = Infinity;
      }
    },
    async handleAddRessource(newResource) {
      if (!newResource) return;
      const added = await addRessource(newResource);
      if (added) this.ressources.push(added);
    },
    async handleUpdateRessource(updatedResource) {
      if (!updatedResource) return;
      const index = this.ressources.findIndex(r => r.id === updatedResource.id);
      if (index !== -1) {
        this.ressources.splice(index, 1, { ...updatedResource, isEditing: false });
        await updateRessource(updatedResource);
      }
    },
    async handleDeleteRessource(resourceId) {
      if (!resourceId) return;
      this.ressources = this.ressources.filter(r => r.id !== resourceId);
      await deleteRessource(resourceId);
    },
    resetFilters() {
      this.selectedTeam = "";
      this.selectedSector = "";
      this.selectedService = "";
      this.selectedPosition = "";
    },
    async moveRessourceUp(ressource) {
      const arr = this.filteredResources;
      const idx = arr.findIndex(r => r.id === ressource.id);
      if (idx > 0) {
        const above = arr[idx - 1];

        // Échange les orders
        const tmpOrder = ressource.order;
        ressource.order = above.order;
        above.order = tmpOrder;

        // Échange les positions dans l'array principale
        const mainIdx = this.ressources.findIndex(r => r.id === ressource.id);
        const mainAboveIdx = this.ressources.findIndex(r => r.id === above.id);
        if (mainIdx > -1 && mainAboveIdx > -1) {
          // Swap dans l'array principale
          const temp = this.ressources[mainIdx];
          this.ressources[mainIdx] = this.ressources[mainAboveIdx];
          this.ressources[mainAboveIdx] = temp;
        }

        await updateRessource(ressource);
        await updateRessource(above);
      }
    },
    async moveRessourceDown(ressource) {
      const arr = this.filteredResources;
      const idx = arr.findIndex(r => r.id === ressource.id);
      if (idx !== -1 && idx < arr.length - 1) {
        const below = arr[idx + 1];

        // Échange les orders
        const tmpOrder = ressource.order;
        ressource.order = below.order;
        below.order = tmpOrder;

        // Échange les positions dans l'array principale
        const mainIdx = this.ressources.findIndex(r => r.id === ressource.id);
        const mainBelowIdx = this.ressources.findIndex(r => r.id === below.id);
        if (mainIdx > -1 && mainBelowIdx > -1) {
          // Swap dans l'array principale
          const temp = this.ressources[mainIdx];
          this.ressources[mainIdx] = this.ressources[mainBelowIdx];
          this.ressources[mainBelowIdx] = temp;
        }

        await updateRessource(ressource);
        await updateRessource(below);
      }
    },
  },
  async mounted() {
    this.fetchRessources();
  }
};
</script>

<style scoped>
table {
  border-collapse: separate; /* Nécessaire pour utiliser border-radius */
  border-spacing: 0;
}

/* Arrondir le coin supérieur gauche et supérieur droit du header */
thead tr th:first-child {
  border-top-left-radius: 8px;
}
thead tr th:last-child {
  border-top-right-radius: 8px;
}

/* Coins inférieurs de la dernière ligne */
tbody tr:last-child td:first-child {
  border-bottom-left-radius: 8px;
}
tbody tr:last-child td:last-child {
  border-bottom-right-radius: 8px;
}
</style>